import React, { useState, useEffect } from 'react';
import {
  Modal,
  Grid,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';

import { useTranslation } from 'react-i18next';



const CreateTaskForm = ({
  open,
  onClose,
  projectId,
  teams = [],
  currentUserId,
  currentUserRoleId,
  updateTasks,
  refreshData
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [status, setStatus] = useState('to-do');
  const [assignedUser, setAssignedUser] = useState('');
  const [assignedTeam, setAssignedTeam] = useState('');
  const [members, setMembers] = useState([]); // Members with teams
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [warningMessage, setWarningMessage] = useState('');
  const { t } = useTranslation();
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });


 const isArabic = t.language === 'ar';

  // "Members with teams" verisini backend'den çekiyoruz
  useEffect(() => {
    const fetchMembersWithTeams = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(
          `/api/projects/${projectId}/members-with-teams`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log(response.data);
        setMembers(response.data);
        setFilteredMembers(response.data);
      } catch (error) {
      }
    };

    if (open) {
      fetchMembersWithTeams();
      // Modal açıldığında form alanlarını sıfırla
      setTitle('');
      setDescription('');
      setDueDate('');
      setStartDate('');
      setPriority('medium');
      setStatus('to-do');
      setAssignedUser('');
      setAssignedTeam('');
      setWarningMessage('');
    }
  }, [open, projectId]);

  // Takım seçildiğinde, üyeleri filtreliyoruz
  useEffect(() => {
    if (assignedTeam) {
      // Seçilen takıma ait üyeleri filtrele
      const teamMembers = members.filter((member) =>
        member.teams.some((team) => team.id === assignedTeam)
      );
      setFilteredMembers(teamMembers);

      // Eğer daha önce seçilen kullanıcı yeni takımda değilse uyarı göster
      if (
        assignedUser &&
        !teamMembers.some((member) => member.id === assignedUser)
      ) {
        setWarningMessage(t('warning_select_valid_user'));
        setAssignedUser(''); // Kullanıcıyı sıfırla
      } else {
        setWarningMessage(''); // Uyarıyı temizle
      }
    } else {
      // Takım seçilmediyse tüm üyeleri göster
      setFilteredMembers(members);
      setWarningMessage('');
    }
  }, [assignedTeam, assignedUser, members]);

  const handleTaskSubmit = async () => {
    // End date'in start date'ten önce olup olmadığını kontrol et
    if (new Date(dueDate) < new Date(startDate)) {
      toast.error(t('error_end_date_before_start'), { position: "top-right", autoClose: 3000 });
      return; // Eğer tarih hatalıysa işlemi durdur
    }
  
    const taskData = {
      title,
      description,
      due_date: dueDate,
      start_date: startDate,
      priority,
      status,
      project_id: projectId,
      assigned_user_id: assignedUser || null,
      assigned_team_id: assignedTeam || null,
      created_by_user_id: currentUserId,
    };
  
    try {
      

      const token = localStorage.getItem('token');
      const response = await api.post(
        '/api/tasks',
        taskData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (response.status === 201) {
        const newTask = response.data;
        refreshData();
        toast.success(t('success_task_added'), { position: "top-right", autoClose: 3000 });
        updateTasks((prevTasks) => [...prevTasks, newTask]);
        onClose();
      } else {
        toast.error(t('error_something_wrong'), { position: "top-right", autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(t('error_creating_task'), { position: "top-right", autoClose: 3000 });
    }
  };
  

  return (
    <Modal open={open} onClose={onClose}>
  <div
    style={{
      padding: 20,
      backgroundColor: 'white',
      width: '80vw',
      height: '80vh',
      margin: '5% auto',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    }}
  >
    <ToastContainer />
    <Typography variant="h6">{t('add_new_task')}</Typography>

    <TextField
      label={t('title')}
      fullWidth
      margin="normal"
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      inputProps={{
        dir: isArabic ? 'rtl' : 'ltr',
      }}
      required
    />
    <TextField
      label={t('description')}
      fullWidth
      margin="normal"
      multiline
      rows={4}
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      inputProps={{
        dir: isArabic ? 'rtl' : 'ltr',
      }}
    />

    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField
          label={t('start_date')}
          fullWidth
          margin="normal"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          onKeyDown={(e) => e.preventDefault()}
          required
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label={t('due_date')}
          fullWidth
          margin="normal"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          onKeyDown={(e) => e.preventDefault()}
          required
        />
      </Grid>
    </Grid>

    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>{t('priority')}</InputLabel>
          <Select value={priority} onChange={(e) => setPriority(e.target.value)} label={t('priority')}>
            <MenuItem value="low">{t('priority_low')}</MenuItem>
            <MenuItem value="medium">{t('priority_medium')}</MenuItem>
            <MenuItem value="high">{t('priority_high')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>{t('status')}</InputLabel>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} label={t('status')}>
            <MenuItem value="to-do">{t('status_todo')}</MenuItem>
            <MenuItem value="in-progress">{t('status_inprogress')}</MenuItem>
            <MenuItem value="completed">{t('status_completed')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    <Grid container spacing={2}>
      <Grid item xs={6}>
        {(currentUserRoleId === 1 || currentUserRoleId === 2) && (
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel>{t('assign_to_team')}</InputLabel>
            <Select
              value={assignedTeam}
              onChange={(e) => setAssignedTeam(e.target.value)}
              required={currentUserRoleId === 2}
              label={t('assign_to_team')}
            >
              <MenuItem value="">{t('select_team')}</MenuItem>
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>{t('assign_to_user')}</InputLabel>
          <Select
            value={assignedUser}
            onChange={(e) => setAssignedUser(e.target.value)}
            disabled={currentUserRoleId === 2 && !assignedTeam}
            label={t('assign_to_user')}
          >
            <MenuItem value="">{t('select_user')}</MenuItem>
            {filteredMembers.map((member) => (
              <MenuItem key={member.id} value={member.id}>
                {member.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
    {warningMessage && (
  <Typography color="error" style={{ marginTop: '10px' }}>
    {warningMessage}
  </Typography>
)}
    <div style={{ marginTop: 'auto' }}>
      <Button onClick={onClose} color="secondary">
        {t('cancel')}
      </Button>
      <Button onClick={handleTaskSubmit} color="primary" disabled={
  !title ||
  !startDate ||
  !dueDate ||
  (currentUserRoleId === 2 && !assignedTeam) || // Role 2 için takım seçilmeli
  (currentUserRoleId === 2 && !assignedUser) // Role 2 için kullanıcı seçilmeli
}>
        {t('create_task')}
      </Button>
    </div>
  </div>
</Modal>



  );
};

export default CreateTaskForm;
